import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

// Animation variants optimized for performance and elegance
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.15 }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: 5,
    transition: {
      duration: 0.15,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

/**
 * ConfirmModal - A simple, elegant confirmation dialog
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when closing the modal
 * @param {function} onConfirm - Function to call when confirming the action
 * @param {string} title - Modal title
 * @param {string|React.ReactNode} message - Modal message (can be a string or React node)
 * @param {string} confirmText - Text for the confirm button
 * @param {string} confirmStyle - Tailwind classes for the confirm button
 * @param {string} cancelText - Text for the cancel button
 * @param {string} className - Additional classes for the modal container
 * @param {string} size - Modal size (sm, md, lg)
 */
export const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message,
  confirmText = 'Confirm',
  confirmStyle = 'bg-black hover:bg-gray-800',
  cancelText = 'Cancel',
  className,
  size = 'md'
}) => {
  // Only render when the modal is open
  if (!isOpen) return null;

  // Size variants
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg'
  };

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10011] p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            key="modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "bg-white rounded-xl shadow-lg border border-gray-100 w-full overflow-hidden",
              sizeClasses[size],
              className
            )}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <motion.button
              whileHover={{ scale: 1.1, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="absolute right-3 top-3 sm:right-4 sm:top-4 rounded-full h-7 w-7 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </motion.button>

            {/* Content */}
            <div className="p-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 pr-8 mb-1 sm:mb-2">
                {title}
              </h3>

              <div className="text-gray-600 text-sm sm:text-base mb-6">
                {typeof message === 'string' ? <p>{message}</p> : message}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 sm:gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="px-3.5 py-2 sm:px-4 sm:py-2.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {cancelText}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onConfirm();
                    // Don't close the modal here - let the caller handle it
                    // This allows the caller to show loading states, etc.
                  }}
                  className={cn(
                    "px-3.5 py-2 sm:px-4 sm:py-2.5 text-sm text-white rounded-lg transition-colors", 
                    confirmStyle
                  )}
                >
                  {confirmText}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Use createPortal to render at the document body level
  return createPortal(modalContent, document.body);
};